import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import type { ProductFormData } from '@/lib/validations/products'
import type { AxiosError } from 'axios'
import type { ErrorResponse } from '@/types/common'
import type { ProductInput, ProductItem } from '@/types/product'
import { ProductFileType, ProductPriceType } from '@/types/product'
import {
  categoryQueryOptions,
  productApisQueryOptions,
  productDetailQueryOptions,
  subCategoryQueryOptions,
} from '@/lib/queryOptions'
import FormProduct from '@/components/Products/Form'
import { getTranslation } from '@/lib/utils'
import { ProductService } from '@/services/products'
import { buildCloudinaryUrl } from '@/lib/cloudinary-url'
import { translateText } from '@/lib/translate'

export const Route = createFileRoute(
  '/_authenticated/products/update/$productId',
)({
  loader: ({
    context: { queryClient },
    params: { productId: productSlug },
  }) => {
    return queryClient.ensureQueryData(productDetailQueryOptions(productSlug))
  },
  component: RouteComponent,
})

function RouteComponent() {
  const queryClient = useQueryClient()
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [isTranslating, setIsTranslating] = useState(false)
  const navigate = useNavigate()
  const { productId: productSlug } = Route.useParams()
  const queryDetail = useSuspenseQuery(productDetailQueryOptions(productSlug))
  const existingTranslations = queryDetail.data.data.translations

  const categoryQuery = useQuery(
    categoryQueryOptions({
      page: 1,
      take: 100,
      isSelectAll: true,
      parentId: null,
    }),
  )

  const subCategoryQuery = useQuery(
    subCategoryQueryOptions({
      page: 1,
      take: 100,
      isSelectAll: true,
      parentId: selectedCategoryId,
    }),
  )

  useEffect(() => {
    setSelectedCategoryId(queryDetail.data.data.mainCategoryId || '')
  }, [queryDetail.data.data.mainCategoryId])

  const productApisQuery = useQuery(productApisQueryOptions())

  const productDetail = (data: ProductItem) => {
    const res = data
    const priceVnd = res.prices.find((p) => p.currency === 'vnd')
    const priceUsd = res.prices.find((p) => p.currency === 'usd')

    // Convert from files array if thumbnail/bannerImages/illustrationImages not provided
    const thumbnail =
      res.thumbnail || res.files.find((f) => f.productFileType === 'thumbnail')
    const bannerImages =
      res.bannerImages ||
      res.files.filter((f) => f.productFileType === 'banner')
    const illustrationImages =
      res.illustrationImages ||
      res.files.filter((f) => f.productFileType === 'illustration')

    const detail: ProductFormData = {
      category: res.mainCategoryId,
      thumbnail: thumbnail
        ? [
            {
              id: thumbnail.id,
              src: buildCloudinaryUrl(
                {
                  publicId: thumbnail.publicId,
                  resourceType: thumbnail.resourceType as 'image' | 'video',
                  format: thumbnail.format,
                  folder: thumbnail.folder,
                },
                {
                  cloudName: import.meta.env.VITE_CLOUDNAME as string,
                  secure: true,
                },
              ),
              alt: thumbnail.originalFilename || '',
              publicId: thumbnail.publicId,
              resourceType: thumbnail.resourceType,
              format: thumbnail.format,
              bytes: thumbnail.bytes,
              width: thumbnail.width,
              height: thumbnail.height,
              folder: thumbnail.folder,
              originalFilename: thumbnail.originalFilename || '',
              isCreate: false,
            },
          ]
        : [],
      bannerImages: bannerImages.map((f) => ({
        id: f.id,
        src: buildCloudinaryUrl(
          {
            publicId: f.publicId,
            resourceType: f.resourceType as 'image' | 'video',
            format: f.format,
            folder: f.folder,
          },
          {
            cloudName: import.meta.env.VITE_CLOUDNAME as string,
            secure: true,
          },
        ),
        alt: f.originalFilename || '',
        publicId: f.publicId,
        resourceType: f.resourceType,
        format: f.format,
        bytes: f.bytes,
        width: f.width,
        height: f.height,
        folder: f.folder,
        originalFilename: f.originalFilename || '',
        isCreate: false,
      })),
      illustrationImages: illustrationImages.map((f) => ({
        id: f.id,
        src: buildCloudinaryUrl(
          {
            publicId: f.publicId,
            resourceType: f.resourceType as 'image' | 'video',
            format: f.format,
            folder: f.folder,
          },
          {
            cloudName: import.meta.env.VITE_CLOUDNAME as string,
            secure: true,
          },
        ),
        alt: f.originalFilename || '',
        publicId: f.publicId,
        resourceType: f.resourceType,
        format: f.format,
        bytes: f.bytes,
        width: f.width,
        height: f.height,
        folder: f.folder,
        originalFilename: f.originalFilename || '',
        isCreate: false,
      })),
      stt: res.code,
      name: getTranslation(res.translations, 'name'),
      englishName: getTranslation(res.translations, 'name', 'en'),
      nameMode:
        getTranslation(res.translations, 'name') ===
        getTranslation(res.translations, 'name', 'en')
          ? 'same'
          : 'separate',
      api1: res.apis[0].id,
      api2: res.apis[1]?.id || '',
      api3: res.apis[2]?.id || '',
      priceType: res.prices[0]?.type || '',
      appUpdateSupportCount: res.updateSupportQuota.toString() || '',
      appUpdateSupportDuration: res.updateSupportPeriodMonths.toString() || '',
      guideId: '',
      displayStatus: res.status,
      productCondition: res.releaseStatus,
      pinStatus: res.pinStatus,
      subCategoryId: res.subCategories[0]?.id || '',
      hashtag: (res.hashTags as any) || [],
      shortDescription: getTranslation(res.translations, 'summary'),
      description: getTranslation(res.translations, 'description'),
      vnd: {
        appPrice: priceVnd?.originalAmount.toString() || '',
        sellingPrice: priceVnd?.amount.toString() || '',
        discount: priceVnd?.discountPercent.toString() || '0',
      },
      usd: {
        appPrice: priceUsd?.originalAmount.toString() || '',
        sellingPrice: priceUsd?.amount.toString() || '',
        discount: priceUsd?.discountPercent.toString() || '0',
      },
    }

    return detail
  }

  const apiOptions =
    productApisQuery.data?.data.map((api) => ({
      value: api.id,
      label: api.name,
      url: api.url,
    })) || []

  const isLoading =
    categoryQuery.isLoading ||
    subCategoryQuery.isLoading ||
    productApisQuery.isLoading ||
    queryDetail.isLoading

  const mutationEditProduct = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ProductInput> }) => {
      return ProductService.update(id, body)
    },
    onSuccess: () => {
      toast.success('Cập nhật sản phẩm thành công')
      queryClient.invalidateQueries({
        queryKey: ['product', productSlug],
      })
      queryClient.invalidateQueries({
        queryKey: ['products'],
      })
      navigate({ to: '/products' })
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message)
    },
  })

  const handleSubmit = async (
    changedData: Partial<ProductFormData>,
    removeFileIds: {
      removeThumbnailId: string | null
      removeBannerImageIds: string[]
      removeIllustrationImageIds: string[]
    },
    form: ProductFormData,
  ) => {
    const payload: Partial<ProductInput> = {}
    const priceVnd = form.vnd
    const priceUsd = form.usd

    // Map category to mainCategoryId
    if (changedData.category) {
      payload.mainCategoryId = changedData.category
    }

    // Image mapping logic
    const createThumbnail = form.thumbnail.filter((file) => file.isCreate)
    if (createThumbnail.length > 0) {
      payload.createThumbnail = {
        publicId: createThumbnail[0].publicId,
        resourceType: createThumbnail[0].resourceType,
        format: createThumbnail[0].format,
        bytes: createThumbnail[0].bytes || 0,
        width: createThumbnail[0].width || 0,
        height: createThumbnail[0].height || 0,
        folder: createThumbnail[0].folder,
        originalFilename: createThumbnail[0].originalFilename,
        productFileType: ProductFileType.THUMBNAIL,
      }
    }

    const createBannerImages = (form.bannerImages || []).filter(
      (file) => file.isCreate,
    )
    if (createBannerImages.length > 0) {
      payload.createBannerImages = createBannerImages.map((file) => ({
        publicId: file.publicId,
        resourceType: file.resourceType,
        format: file.format,
        bytes: file.bytes || 0,
        width: file.width || 0,
        height: file.height || 0,
        folder: file.folder,
        originalFilename: file.originalFilename,
        productFileType: ProductFileType.BANNER,
      }))
    }

    const createIllustrationImages = (form.illustrationImages || []).filter(
      (file) => file.isCreate,
    )
    if (createIllustrationImages.length > 0) {
      payload.createIllustrationImages = createIllustrationImages.map(
        (file) => ({
          publicId: file.publicId,
          resourceType: file.resourceType,
          format: file.format,
          bytes: file.bytes || 0,
          width: file.width || 0,
          height: file.height || 0,
          folder: file.folder,
          originalFilename: file.originalFilename,
          productFileType: ProductFileType.ILLUSTRATION,
        }),
      )
    }

    payload.prices =
      form.priceType === ProductPriceType.MANUAL
        ? [
            {
              currency: 'vnd',
              type: form.priceType,
              originalAmount: priceVnd.appPrice ? Number(priceVnd.appPrice) : 0,
              amount: priceVnd.sellingPrice ? Number(priceVnd.sellingPrice) : 0,
              discountPercent: priceVnd.discount
                ? Number(priceVnd.discount)
                : 0,
            },
            {
              currency: 'usd',
              type: form.priceType,
              originalAmount: priceUsd.appPrice ? Number(priceUsd.appPrice) : 0,
              amount: priceUsd.sellingPrice ? Number(priceUsd.sellingPrice) : 0,
              discountPercent: priceUsd.discount
                ? Number(priceUsd.discount)
                : 0,
            },
          ]
        : []

    if (
      changedData.api1 !== undefined ||
      changedData.api2 !== undefined ||
      changedData.api3 !== undefined
    ) {
      payload.apiIds = [form.api1, form.api2, form.api3].filter(
        (id): id is string => !!id,
      )
    }

    const englishExisting = existingTranslations.find((t) =>
      t.language.toLowerCase().startsWith('en'),
    )

    const viName =
      form.nameMode === 'same'
        ? form.englishName || ''
        : form.name || form.englishName || ''
    const viSummary = form.shortDescription || ''
    const viDescription = form.description || ''

    const englishName = form.englishName || viName

    const translations: ProductInput['translations'] = [
      {
        language: 'vi',
        name: viName,
        summary: form.shortDescription || '',
        description: form.description || '',
      },
    ]
    let englishSummary = englishExisting?.summary ?? ''
    let englishDescription = englishExisting?.description ?? ''

    const hasEnglishTranslation = Boolean(englishExisting)

    const shouldTranslateSummary =
      (!hasEnglishTranslation || changedData.shortDescription !== undefined) &&
      viSummary
    const shouldTranslateDescription =
      (!hasEnglishTranslation || changedData.description !== undefined) &&
      viDescription

    if (shouldTranslateSummary || shouldTranslateDescription) {
      setIsTranslating(true)
      try {
        const [summaryEn, descriptionEn] = await Promise.all([
          shouldTranslateSummary
            ? translateText(viSummary, { source: 'vi', target: 'en' })
            : Promise.resolve(''),
          shouldTranslateDescription
            ? translateText(viDescription, { source: 'vi', target: 'en' })
            : Promise.resolve(''),
        ])

        if (shouldTranslateSummary) {
          englishSummary = summaryEn.trim() ? summaryEn : viSummary
        }
        if (shouldTranslateDescription) {
          englishDescription = descriptionEn.trim()
            ? descriptionEn
            : viDescription
        }
      } catch (error) {
        console.error('Translation failed', error)
        toast.error('Không thể dịch sang tiếng Anh. Vui lòng thử lại.')
        if (shouldTranslateSummary) {
          englishSummary = viSummary
        }
        if (shouldTranslateDescription) {
          englishDescription = viDescription
        }
      } finally {
        setIsTranslating(false)
      }
    }

    if (viName || englishSummary || englishDescription) {
      translations.push({
        language: 'en',
        name: englishName || viName,
        summary: englishSummary,
        description: englishDescription,
      })
    }

    payload.translations = translations

    if (removeFileIds.removeThumbnailId) {
      payload.removeThumbnailId = removeFileIds.removeThumbnailId
    }
    if (removeFileIds.removeBannerImageIds.length > 0) {
      payload.removeBannerImageIds = removeFileIds.removeBannerImageIds
    }
    if (removeFileIds.removeIllustrationImageIds.length > 0) {
      payload.removeIllustrationImageIds =
        removeFileIds.removeIllustrationImageIds
    }

    console.log('Only changed fields payload:', payload)
    try {
      await mutationEditProduct.mutateAsync({
        id: queryDetail.data.data.id,
        body: payload as ProductInput,
      })
    } catch (error) {
      console.error('Update product failed', error)
    }
  }

  return (
    <FormProduct
      initialData={productDetail(queryDetail.data.data)}
      onSubmit={handleSubmit}
      setSelectedCategoryId={setSelectedCategoryId}
      categories={categoryQuery.data?.data.items || []}
      subCategories={subCategoryQuery.data?.data.items || []}
      productApis={apiOptions}
      isLoading={isLoading}
      disableSubmit={mutationEditProduct.isPending || isTranslating}
      label="Cập nhật sản phẩm"
      isCreate={false}
    />
  )
}
